import axios from 'axios';

const url = process.env.VUE_APP_API_BASE_URL;

// Función auxiliar para mapear estados de la interfaz a la API
const mapEstadoToApi = (estado) => {
  const mapping = {
    "Sin Asignar": "pendiente_asignar",
    "Asignado": "asignado",
    "Entregado": "entregado",
    "Devuelto": "devuelto",
    "Revisar Devolución": "pendiente_aceptar_devolucion",
    "Cerrado": "cerrado",
    "Cerrado sin tarea": "cerrado_sin_tarea",
    "Material Abandonado": "material_abandonado",
    "MAT. ABANDONADO": "material_abandonado"
  };
  return mapping[estado];
};

// Función auxiliar para verificar si es un mes completo
const esMesCompleto = (fdesde, fhasta) => {
  try {
    const inicio = new Date(fdesde);
    const fin = new Date(fhasta);
    
    // Verificar si el inicio es el día 1 del mes
    if (inicio.getDate() !== 1) return false;
    
    // Verificar si el fin es el último día del mes
    const ultimoDiaDelMes = new Date(inicio.getFullYear(), inicio.getMonth() + 1, 0);
    if (fin.getDate() !== ultimoDiaDelMes.getDate()) return false;
    
    // Verificar que sean del mismo mes y año
    if (inicio.getMonth() !== fin.getMonth() || inicio.getFullYear() !== fin.getFullYear()) return false;
    
    return true;
  } catch (error) {
    console.error('Error verificando mes completo:', error);
    return false;
  }
};

// Función auxiliar para el endpoint de mes
const listMonthEndpoint = async (commit, params) => {
  const queryParams = {};
  
  // Parámetro obligatorio: mes en formato YYYY-MM
  if (params.fdesde && params.fhasta) {
    const inicio = new Date(params.fdesde);
    const mes = `${inicio.getFullYear()}-${String(inicio.getMonth() + 1).padStart(2, '0')}`;
    queryParams.mes = mes;
  }
  
  // Filtro de búsqueda
  if (params.query && params.query.trim() !== '') {
    queryParams.search = params.query.trim();
    console.log('🔍 [STORE] Pasando parámetro search a listMonthEndpoint:', queryParams.search);
  } else {
    console.log('🔍 [STORE] No hay query o está vacío en listMonthEndpoint');
  }
  
  // Filtro de estado
  if (params.estado && params.estado !== "Todos") {
    const estadoApi = mapEstadoToApi(params.estado);
    if (estadoApi) {
      queryParams.estado = estadoApi;
    }
  }
  
  // Filtro por tarea asociada
  if (params.tareaAsociada && params.tareaAsociada !== "Todos") {
    queryParams.tareaAsociada = params.tareaAsociada;
  }
  
  // Filtro por clon
  if (params.esClon && params.esClon !== "Todos") {
    if (params.esClon === "Sí") {
      queryParams.esClon = true;
    } else if (params.esClon === "No") {
      queryParams.esClon = false;
    }
  }
  
  // Orden de resultados - por defecto ordenar por fecha_entrega descendente (más reciente primero)
  if (params.sort && params.sort.length > 0) {
    const sortField = params.sort[0];
    queryParams.sortOrder = sortField.includes('desc') ? 'desc' : 'asc';
  } else {
    // Si no se especifica ordenación, usar fecha_entrega descendente por defecto
    queryParams.sortOrder = 'desc';
  }
  // Asegurar que siempre se ordene por fecha_entrega
  queryParams.sortField = 'fecha_entrega';
  
  console.log('🔍 [STORE] Usando endpoint albaran-activo-list/month con parámetros:', queryParams);
  
  try {
    const response = await axios.get(`${url}/albaran-activo-list/month`, { params: queryParams });
    
    console.log('🔍 [STORE] Respuesta del endpoint albaran-activo-list/month:', {
      total: response.data.stats?.total || 0,
      albaranes: response.data.albaranes?.length || 0,
      mes: response.data.stats?.mes || 'N/A',
      nombreMes: response.data.stats?.nombreMes || 'N/A'
    });
    
    commit("setList", response.data.albaranes || []);
    commit("setListTotal", response.data.stats?.total || 0);
    
    return {
      albaranes: response.data.albaranes || [],
      stats: response.data.stats || {}
    };
  } catch (error) {
    console.error('Error usando endpoint albaran-activo-list/month, fallback a endpoint estándar:', error);
    // Fallback al endpoint estándar si hay error
    return await listStandardEndpoint(commit, params);
  }
};

// Función auxiliar para el endpoint estándar
const listStandardEndpoint = async (commit, params) => {
  const queryParams = {
    populate: [
      'users_permissions_user', 
      'users_permissions_user.imagen_perfil',
      'users_permissions_user.nombre',
      'users_permissions_user.apellidos',
      'users_permissions_user.email',
      'albaran_activo_materials',
      'albaran_activo_materials.activo',
      'albaran_activo_materials.usuario_accion',
      'albaran_activo_materials.material_taller'
    ]
  };

  // Construir filtros
  const filters = {};
  
  // Filtro de búsqueda
  if (params.query) {
    filters.$or = [
      { numero: { $containsi: params.query } },
      { cliente: { $containsi: params.query } },
      { titulo: { $containsi: params.query } },
      { descripcion: { $containsi: params.query } }
    ];
  }

  // Filtros de fecha
  if (params.fdesde || params.fhasta) {
    filters.fecha_entrega = {};
    if (params.fdesde) {
      const fromDate = params.fdesde.split("-").reverse().join("-");
      filters.fecha_entrega.$gte = fromDate;
    }
    if (params.fhasta) {
      const toDate = params.fhasta.split("-").reverse().join("-");
      filters.fecha_entrega.$lte = toDate;
    }
  }

  // Filtro de estado
  if (params.estado && params.estado !== "Todos") {
    const estadoApi = mapEstadoToApi(params.estado);
    if (estadoApi) {
      filters.estado = estadoApi;
    }
  }

  // Filtro por tarea asociada
  if (params.tareaAsociada && params.tareaAsociada !== "Todos") {
    if (params.tareaAsociada === "Con tarea") {
      filters.tarea_keikaku_id = { $notNull: true };
    } else if (params.tareaAsociada === "Sin tarea") {
      filters.tarea_keikaku_id = { $null: true };
    }
  }

  // Filtro por clon
  if (params.esClon && params.esClon !== "Todos") {
    if (params.esClon === "Sí") {
      filters.es_clon = true;
    } else if (params.esClon === "No") {
      filters.es_clon = false;
    }
  }

  // Aplicar filtros si existen
  if (Object.keys(filters).length > 0) {
    queryParams.filters = filters;
    console.log('🔍 [STORE] Filtros aplicados (endpoint estándar):', filters);
  }

  // Parámetros de paginación
  if (params.page) {
    queryParams['pagination[page]'] = params.page;
  }
  if (params.pageSize) {
    queryParams['pagination[pageSize]'] = params.pageSize;
  }

  // Parámetros de ordenación - por defecto ordenar por fecha_entrega descendente (más reciente primero)
  if (params.sort && params.sort.length > 0) {
    const sortField = params.sort[0];
    const field = sortField.replace(':asc', '').replace(':desc', '');
    const order = sortField.includes('desc') ? 'desc' : 'asc';
    queryParams.sort = `${field}:${order}`;
  } else {
    // Si no se especifica ordenación, usar fecha_entrega descendente por defecto
    queryParams.sort = 'fecha_entrega:desc';
  }

  const response = await axios.get(`${url}/albaran-activos`, { params: queryParams });
  
  console.log('🔍 [STORE] Respuesta del API estándar:', {
    total: response.data.meta.pagination.total,
    albaranes: response.data.data.length,
    fechas: response.data.data.map(a => a.attributes.fecha_entrega)
  });
  
  commit("setList", response.data.data);
  commit("setListTotal", response.data.meta.pagination.total);
  
  // Devolver estructura compatible con el componente
  return {
    albaranes: response.data.data || [],
    stats: {
      total: response.data.meta.pagination.total || 0,
      totalPendientesAsignar: 0,
      totalAsignados: 0,
      totalCerrados: 0
    }
  };
};

export default {
  namespaced: true,
  state: {
    albaran: {},
    albaranes: [],
    total: 0
  },
  getters: {
    albaran(state) {
      return state.albaran;
    },
    list(state) {
      return state.albaranes;
    },
    listTotal(state) {
      return state.total;
    }
  },
  mutations: {
    setAlbaran(state, albaran) {
      state.albaran = albaran;
    },
    setList(state, albaranes) {
      state.albaranes = albaranes;
    },
    setListTotal(state, total) {
      state.total = total;
    }
  },
  actions: {
    async get({ commit }, id) {
      // Usamos el nuevo endpoint que devuelve el detalle completo
      const response = await axios.get(`${url}/albaran-activo-detalle/${id}`);
      console.log('Respuesta albaran-activo-detalle/get:', response.data);
      commit("setAlbaran", response.data.data);
      return response.data.data;
    },
    async list({ commit }, params) {
      // Usar el endpoint específico para listado con filtros
      const queryParams = {};

      // Filtro de fecha de entrega (parámetro específico del endpoint)
      if (params.fdesde && params.fhasta) {
        if (params.fdesde === params.fhasta) {
          // Si fdesde y fhasta son iguales, usar fechaEntrega
          queryParams.fechaEntrega = params.fdesde;
        } else {
          // Si hay rango de fechas, verificar si es un mes completo
          const mesCompleto = esMesCompleto(params.fdesde, params.fhasta);
          if (mesCompleto) {
            // Usar endpoint específico para mes completo
            return await listMonthEndpoint(commit, params);
          } else {
            // Usar endpoint estándar para rangos de fechas
            return await listStandardEndpoint(commit, params);
          }
        }
      }

      // Filtro de búsqueda
      if (params.query && params.query.trim() !== '') {
        queryParams.search = params.query.trim();
        console.log('🔍 [STORE] Pasando parámetro search a albaran-activo-list:', queryParams.search);
      } else {
        console.log('🔍 [STORE] No hay query o está vacío en albaran-activo-list');
      }

      // Filtro de estado
      if (params.estado && params.estado !== "Todos") {
        const estadoApi = mapEstadoToApi(params.estado);
        if (estadoApi) {
          queryParams.estado = estadoApi;
        }
      }

      // Filtro por tarea asociada
      if (params.tareaAsociada && params.tareaAsociada !== "Todos") {
        queryParams.tareaAsociada = params.tareaAsociada;
      }

      // Filtro por tipo de albarán (clon o original)
      if (params.esClon && params.esClon !== "Todos") {
        queryParams.esClon = params.esClon;
      }

      // Ordenamiento - por defecto ordenar por fecha_entrega descendente (más reciente primero)
      if (params.sort && params.sort.length > 0) {
        const sortField = params.sort[0];
        if (sortField.includes('desc')) {
          queryParams.sortOrder = 'desc';
        } else {
          queryParams.sortOrder = 'asc';
        }
      } else {
        // Si no se especifica ordenación, usar fecha_entrega descendente por defecto
        queryParams.sortOrder = 'desc';
      }
      // Asegurar que siempre se ordene por fecha_entrega
      queryParams.sortField = 'fecha_entrega';

      console.log('🔍 [STORE] Usando endpoint albaran-activo-list con parámetros:', queryParams);

      try {
        const response = await axios.get(`${url}/albaran-activo-list`, { params: queryParams });
        
        console.log('🔍 [STORE] Respuesta del endpoint albaran-activo-list:', {
          total: response.data.stats?.total || 0,
          albaranes: response.data.albaranes?.length || 0,
          fechas: response.data.albaranes?.map(a => a.fecha_entrega) || []
        });
        
        // El endpoint devuelve la estructura: { albaranes: [], stats: {} }
        commit("setList", response.data.albaranes || []);
        commit("setListTotal", response.data.stats?.total || 0);
        
        // Devolver también las estadísticas para los KPIs
        return {
          albaranes: response.data.albaranes || [],
          stats: response.data.stats || {}
        };
      } catch (error) {
        console.error('Error usando endpoint albaran-activo-list, fallback a endpoint estándar:', error);
        // Fallback al endpoint estándar si hay error
        return await listStandardEndpoint(commit, params);
      }
    },

    async listStandard({ commit }, params) {
      // Usar la función auxiliar para el endpoint estándar
      return await listStandardEndpoint(commit, params);
    },

    async listStandardOld({ commit }, params) {
      // Endpoint estándar como fallback (método anterior)
      const queryParams = {
        populate: [
          'users_permissions_user', 
          'users_permissions_user.imagen_perfil',
          'users_permissions_user.nombre',
          'users_permissions_user.apellidos',
          'users_permissions_user.email',
          'albaran_activo_materials',
          'albaran_activo_materials.activo',
          'albaran_activo_materials.usuario_accion',
          'albaran_activo_materials.material_taller'
        ]
      };

      // Construir filtros
      const filters = {};
      
      // Filtro de búsqueda
      if (params.query) {
        filters.$or = [
          { numero: { $containsi: params.query } },
          { cliente: { $containsi: params.query } },
          { titulo: { $containsi: params.query } },
          { descripcion: { $containsi: params.query } }
        ];
      }

      // Filtros de fecha
      if (params.fdesde || params.fhasta) {
        filters.fecha_entrega = {};
        if (params.fdesde) {
          const fromDate = params.fdesde.split("-").reverse().join("-");
          filters.fecha_entrega.$gte = fromDate;
        }
        if (params.fhasta) {
          const toDate = params.fhasta.split("-").reverse().join("-");
          filters.fecha_entrega.$lte = toDate;
        }
      }

      // Filtro de estado
      if (params.estado && params.estado !== "Todos") {
        const estadoApi = mapEstadoToApi(params.estado);
        if (estadoApi) {
          filters.estado = estadoApi;
        }
      }

      // Filtro por tarea asociada
      if (params.tareaAsociada && params.tareaAsociada !== "Todos") {
        if (params.tareaAsociada === "Con tarea") {
          filters.tarea_keikaku_id = { $notNull: true };
        } else if (params.tareaAsociada === "Sin tarea") {
          filters.tarea_keikaku_id = { $null: true };
        }
      }

      // Filtro por tipo de albarán (clon o original)
      if (params.esClon && params.esClon !== "Todos") {
        filters.es_clon = params.esClon === "true";
      }

      // NUEVO: Manejar filtros directos (para verificación de duplicados)
      // Solo procesar filtros si no estamos en modo de añadir albarán
      if (params.filters && !params.skipFilters) {
        Object.assign(filters, params.filters);
      }

      // Añadir filtros a los parámetros de consulta si hay alguno activo
      if (Object.keys(filters).length > 0) {
        queryParams.filters = filters;
        console.log('🔍 [STORE] Filtros aplicados (endpoint estándar):', filters);
      }

      // Añadir ordenamiento si está presente
      if (params.sort) {
        queryParams.sort = params.sort;
      }

      // Añadir paginación si está presente
      if (params.pagination?.page) {
        queryParams['pagination[page]'] = params.pagination.page;
      }
      if (params.pagination?.pageSize) {
        queryParams['pagination[pageSize]'] = params.pagination.pageSize;
      }

      const response = await axios.get(`${url}/albaran-activos`, { params: queryParams });
      
      console.log('🔍 [STORE] Respuesta del API estándar:', {
        total: response.data.meta.pagination.total,
        albaranes: response.data.data.length,
        fechas: response.data.data.map(a => a.attributes.fecha_entrega)
      });
      
      commit("setList", response.data.data);
      commit("setListTotal", response.data.meta.pagination.total);
      
      // Devolver estructura compatible con el componente
      return {
        albaranes: response.data.data || [],
        stats: {
          total: response.data.meta.pagination.total || 0,
          totalPendientesAsignar: 0,
          totalAsignados: 0,
          totalCerrados: 0
        }
      };
    },


    async add({ commit }, data) {
      const response = await axios.post(`${url}/albaran-activos`, { data });
      return response.data.data;
    },
    async update({ commit }, { id, data }) {
      const response = await axios.put(`${url}/albaran-activos/${id}`, { data });
      return response.data.data;
    },
    async destroy({ commit }, id) {
      const response = await axios.delete(`${url}/albaran-activos/${id}`);
      return response.data.data;
    },
    async addActivo({ commit }, { albaranId, activoId }) {
      // Primero obtenemos el albarán actual para obtener los activos existentes
      const albaran = await axios.get(`${url}/albaran-activos/${albaranId}?populate=*`);
      
      // Obtenemos los IDs de los activos actuales
      const activosActuales = albaran.data.data.attributes.activos.data || [];
      const activosIds = activosActuales.map(a => a.id);
      
      // Añadimos el nuevo activo
      activosIds.push(activoId);
      
      // Actualizamos el albarán con la nueva lista de activos
      const response = await axios.put(`${url}/albaran-activos/${albaranId}`, {
        data: {
          activos: activosIds
        }
      });
      
      return response.data.data;
    }
  }
}; 